import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type MouseEvent as ReactMouseEvent,
} from 'react'
import { useVirtualizer } from '@tanstack/react-virtual'
import { Button } from '@cottage/design-system/internal-tooling/ui'
import SearchableSelect, { type SelectOption } from '@/components/SearchableSelect'
import UnderlineTabNav from '@/components/UnderlineTabNav'
import { useDebounce } from '@/hooks/useDebounce'
import { useMaxMd } from '@/hooks/useMaxMd'
import {
  createPurchasingProduct,
  getPurchasingMetrics,
  getPurchasingReviewQueue,
  listPurchasingMatchEvents,
  listPurchasingProducts,
  matchPurchasingBulk,
  matchPurchasingVendorItem,
  patchPurchasingPurchaseLine,
  patchPurchasingPurchaseLinesBulk,
  searchPurchasingMeezIngredients,
  suggestPurchasingVendorItem,
  unlinkPurchasingVendorItem,
  updatePurchasingProduct,
  type PurchasingVendorItemMatchRequest,
} from '@/services/purchasing'
import type {
  PurchasingMeezIngredientRow,
  PurchasingReviewQueueRow,
  PurchasingVendorItemMatchResponse,
} from '@/services/purchasingTypes'
import { dismissToast, toast } from '@/store/toastStore'
import { usePurchasingNavStore } from '@/store/purchasingNavStore'
import { formatApiError, getApiErrorDetail, parseDuplicateCandidates } from '@/utils/apiError'
import { foldForSearch } from '@/utils/foldForSearch'
import { formatChicagoDateTime } from '@/utils/dateFormat'
import {
  distinctFieldOptions,
  filterRowsByText,
  filterRowsWithTree,
} from '@/pages/fixed-assets/fixedAssetsTableFilters'
import { exportPurchasingTableCsv, exportPurchasingTableToGoogleSheet } from './purchasingTableExport'
import PurchasingCreateProductDialog from './PurchasingCreateProductDialog'
import PurchasingDuplicateProductDialog from './PurchasingDuplicateProductDialog'
import PurchasingVendorItemPackDialog from './PurchasingVendorItemPackDialog'
import PurchasingReviewBulkActionBar from './PurchasingReviewBulkActionBar'
import PurchasingReviewKeyboardShortcutsModal from './PurchasingReviewKeyboardShortcutsModal'
import PurchasingSourceInvoiceModal from './PurchasingSourceInvoiceModal'
import {
  buildItemIndexByTableRowKey,
  buildReviewTableRows,
  displayNavItems,
  stickyGroupHeaderAt,
  tableRowKey,
  virtualIndexForNavIndex,
  REVIEW_GROUP_ROW_HEIGHT,
  REVIEW_ITEM_ROW_HEIGHT,
  type ReviewNavItem,
  type ReviewTableRow,
} from './purchasingReviewGroups'
import {
  handlePurchasingReviewKeyDown,
  nextCursorIndex,
  type PurchasingReviewKeyboardActions,
  type PurchasingReviewKeyboardContext,
} from './purchasingReviewKeyboardShortcuts'
import {
  ActiveFilterPillBar,
  ColumnPicker,
  ColumnResizeHandle,
  FilterBuilderDrawer,
  GroupBySelect,
  HoverHelp,
  SearchInput,
  SortBuilder,
  TablePageShell,
  TableToolbar,
  applySorts,
  countFilterConditions,
  generateFilterPills,
  getDefaultColumnVisibility,
  getVisibleFieldKeys,
  loadColumnVisibility,
  removeFilterPill,
  saveColumnVisibility,
  useColumnWidths,
  type ColumnVisibility,
  type FilterGroup,
  type SortSpec,
} from '@/tableControls'
import {
  applyLinePatchLocally,
  applyPackSavedLocally,
  applyUndoDoneLocally,
  applyOptimisticProductLinkToQueue,
  applyVendorItemMatchLocally,
  applyVendorItemUnlinkLocally,
  buildMatchRequestFromRow,
  buildOptimisticProductLinkPatch,
  buildReasonsForQueueRow,
  buildPurchasingReviewExportMatrix,
  buildPurchasingReviewFilterFields,
  buildReviewCatalogOptionsForRow,
  canClearProductSelection,
  canConfirmProductLink,
  clampFocusedRowIndex,
  collectReviewReasonFilterValuesForRows,
  dedupeReviewQueueRows,
  dropVendorItemRowsLeavingNeedsReview,
  fixedColumnStyle,
  flagBadgeClass,
  formatCostPerAtomic,
  isReviewPinnedColumn,
  meezRowToSelectOption,
  mergeReviewSelectOptions,
  productRowToSelectOption,
  PURCHASING_REVIEW_PAGE_KEY,
  reasonLabel,
  removeLineFromQueue,
  collectVendorItemSnapshotPlacements,
  removeLinesFromQueue,
  restoreLinesToQueue,
  shouldSkipProductLinkPersistApply,
  reviewPinnedCellClassName,
  reviewPinnedStickyLeft,
  REVIEW_CHECKBOX_COLUMN_WIDTH,
  rowBelongsOnNeedsReview,
  REVIEW_GROUP_BY_OPTIONS,
  REVIEW_QUEUE_COLUMN_KEYS,
  REVIEW_QUEUE_COLUMN_LABELS,
  buildReviewQueueSortFields,
  partitionReviewSortSpecs,
  REVIEW_QUEUE_DEFAULT_SORT,
  REVIEW_QUEUE_DEFAULT_WIDTHS,
  REVIEW_QUEUE_FIELD_DEFINITIONS,
  reviewQueueSearchText,
  reviewBulkActionsForAllRows,
  reviewRowHasFlagActions,
  queueRowReasonBadges,
  queueRowReasons,
  type PurchasingReviewFilterContext,
  resolveRowProductAtomicUom,
  rowIsBulkApprovable,
  rowHasPersistedProductLink,
  preferExistingCatalogOption,
  productLinkConfirmButtonState,
  selectionFromProductOptionId,
  defaultSuggestionOptionId,
  uomsConflict,
  vendorItemNeedsProduct,
  type ProductLinkOptimisticContext,
  type ReviewGroupBy,
  type ReviewQueueColumnKey,
  type ReviewSelection,
} from './purchasingReviewTableConfig'
import {
  formatQboVendorLabel,
  qboVendorFilterOptions,
  useQboVendorDisplayNameMap,
} from './purchasingVendorNames'
import {
  purchasingMatchStatusBadge,
  renderPurchasingStatusBadge,
} from './purchasingStatusBadges'

type ReviewSegment = 'needs_review' | 'done'

function isAutoLinkedReviewRow(row: PurchasingReviewQueueRow): boolean {
  return row.match_status === 'auto'
}

function isVendorItemAlreadyUnlinkedError(error: unknown): boolean {
  const detail = getApiErrorDetail(error)
  return typeof detail === 'string' && detail.includes('not linked')
}

function formatMoney(value: string | null | undefined): string {
  if (!value) return '—'
  const num = Number(value)
  if (!Number.isFinite(num)) return value
  return num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })
}

function formatDateYmd(value: string | null | undefined): string {
  if (!value) return '—'
  return value.slice(0, 10)
}

function uomConflictToastMessage(
  packUom: string | null | undefined,
  productUom: string | null | undefined,
): string {
  return `Product linked, but pack UOM (${packUom ?? '?'}) does not match product UOM (${productUom ?? '?'}). Edit the pack or choose a different product.`
}

function uomConflictToastFromMatch(
  result: PurchasingVendorItemMatchResponse,
  row: PurchasingReviewQueueRow,
  productAtomicUom: string | null,
): string {
  return uomConflictToastMessage(
    result.pack_atomic_uom ?? row.atomic_uom,
    result.product_atomic_uom ?? productAtomicUom,
  )
}

function productAtomicUomFromCatalog(
  productId: number,
  catalogProducts: Array<{ id: number; atomic_uom: string }>,
): string | null {
  return catalogProducts.find((product) => product.id === productId)?.atomic_uom ?? null
}

const PURCHASING_MEEZ_CATALOG_LIMIT = 2000

export default function PurchasingReview() {
  const maxMd = useMaxMd()
  const allowColumnResize = !maxMd

  const [segment, setSegment] = useState<ReviewSegment>('needs_review')
  const [queueRows, setQueueRows] = useState<PurchasingReviewQueueRow[]>([])
  const [, setNextCursor] = useState<string | null>(null)
  const [loadingQueue, setLoadingQueue] = useState(true)
  const [backgroundLoading, setBackgroundLoading] = useState(false)
  const [metricsTotals, setMetricsTotals] = useState<Record<string, number> | null>(null)

  const [textSearch, setTextSearch] = useState('')
  const debouncedReviewSearch = useDebounce(textSearch, 200)
  const reviewSearchFetchGenRef = useRef(0)
  const reviewCrawlGenRef = useRef(0)
  const crawlCursorRef = useRef<string | null>(null)
  const searchInputId = 'purchasing-review-search'
  const [filterTree, setFilterTree] = useState<FilterGroup | null>(null)
  const [sortSpecs, setSortSpecs] = useState<SortSpec[]>(REVIEW_QUEUE_DEFAULT_SORT)
  const [groupBy, setGroupBy] = useState<ReviewGroupBy>('none')
  const [showFilters, setShowFilters] = useState(false)
  const [showSort, setShowSort] = useState(false)
  const [showColumns, setShowColumns] = useState(false)
  const [showShortcuts, setShowShortcuts] = useState(false)
  const [showBulkEdit, setShowBulkEdit] = useState(false)
  const [columnVisibility, setColumnVisibility] = useState<ColumnVisibility>(() =>
    loadColumnVisibility(PURCHASING_REVIEW_PAGE_KEY, REVIEW_QUEUE_FIELD_DEFINITIONS),
  )

  const [selectedRowKeys, setSelectedRowKeys] = useState<Set<string>>(() => new Set())
  const [selectionByVendorItemId, setSelectionByVendorItemId] = useState<
    Record<number, ReviewSelection | null>
  >({})
  const [optionIdByVendorItemId, setOptionIdByVendorItemId] = useState<Record<number, string | null>>({})
  const [busyVendorItemIds, setBusyVendorItemIds] = useState<Set<number>>(() => new Set())

  const setVendorItemBusy = useCallback((vendorItemId: number, busy: boolean) => {
    setBusyVendorItemIds((prev) => {
      const next = new Set(prev)
      if (busy) next.add(vendorItemId)
      else next.delete(vendorItemId)
      return next
    })
  }, [])

  const setVendorItemsBusy = useCallback((vendorItemIds: number[], busy: boolean) => {
    setBusyVendorItemIds((prev) => {
      const next = new Set(prev)
      for (const id of vendorItemIds) {
        if (busy) next.add(id)
        else next.delete(id)
      }
      return next
    })
  }, [])

  const isVendorItemBusy = useCallback(
    (vendorItemId: number) => busyVendorItemIds.has(vendorItemId),
    [busyVendorItemIds],
  )

  const [cursorIndex, setCursorIndexState] = useState<number | null>(null)
  const cursorIndexRef = useRef<number | null>(null)
  const tableRef = useRef<HTMLTableElement>(null)
  const tableRowsRef = useRef<ReviewTableRow[]>([])
  const groupByRef = useRef(groupBy)
  const navItemsRef = useRef<ReviewNavItem[]>([])
  const lastSelectedIndexRef = useRef<number | null>(null)
  const approvingLineIdsRef = useRef(new Set<number>())
  const matchInFlightByVendorItemRef = useRef(new Map<number, Promise<void>>())
  const productLinkEpochByVendorItemRef = useRef(new Map<number, number>())

  const selectedLineIds = useMemo(() => {
    const ids = new Set<number>()
    for (const key of selectedRowKeys) {
      const lineId = Number(key.split('\0')[1])
      if (Number.isFinite(lineId)) ids.add(lineId)
    }
    return ids
  }, [selectedRowKeys])

  const [modalRow, setModalRow] = useState<PurchasingReviewQueueRow | null>(null)
  const [modalDecidedBy, setModalDecidedBy] = useState<string | null>(null)
  const [modalDecidedAt, setModalDecidedAt] = useState<string | null>(null)

  const [createProductRow, setCreateProductRow] = useState<PurchasingReviewQueueRow | null>(null)
  const [createProductName, setCreateProductName] = useState('')
  type DuplicateConflictState =
    | {
        kind: 'match'
        row: PurchasingReviewQueueRow
        payload: PurchasingVendorItemMatchRequest
        candidates: { id: number; name: string }[]
      }
    | {
        kind: 'create'
        row: PurchasingReviewQueueRow
        payload: Parameters<typeof createPurchasingProduct>[0]
        candidates: { id: number; name: string }[]
      }
  const [duplicateConflict, setDuplicateConflict] = useState<DuplicateConflictState | null>(null)
  const [duplicateConflictBusy, setDuplicateConflictBusy] = useState(false)
  const [catalogProducts, setCatalogProducts] = useState<
    Awaited<ReturnType<typeof listPurchasingProducts>>['rows']
  >([])
  const [catalogMeezIngredients, setCatalogMeezIngredients] = useState<PurchasingMeezIngredientRow[]>(
    [],
  )

  const [bulkEditQty, setBulkEditQty] = useState('')
  const [bulkEditUnitPrice, setBulkEditUnitPrice] = useState('')
  const [bulkEditPack, setBulkEditPack] = useState('')
  const [bulkLinkRow, setBulkLinkRow] = useState<PurchasingReviewQueueRow | null>(null)
  const [packEditorTarget, setPackEditorTarget] = useState<{
    vendorItemIds: number[]
    initialPackText: string | null
  } | null>(null)

  const [isExporting, setIsExporting] = useState(false)
  const [catalogSearch, setCatalogSearch] = useState('')
  const debouncedCatalogSearch = useDebounce(catalogSearch, 300)
  const [fetchedCatalogOptions, setFetchedCatalogOptions] = useState<SelectOption[]>([])
  const [createdProductOptions, setCreatedProductOptions] = useState<SelectOption[]>([])

  const { getWidth, startResize } = useColumnWidths({
    pageKey: PURCHASING_REVIEW_PAGE_KEY,
    columnKeys: [...REVIEW_QUEUE_COLUMN_KEYS],
    defaults: REVIEW_QUEUE_DEFAULT_WIDTHS,
  })

  const vendorNameMap = useQboVendorDisplayNameMap()

  useEffect(() => {
    groupByRef.current = groupBy
  }, [groupBy])

  const crawlBackgroundPages = useCallback(
    async (crawlGen: number, startCursor: string) => {
      let cursor: string | undefined = startCursor
      setBackgroundLoading(true)
      try {
        while (cursor) {
          if (crawlGen !== reviewCrawlGenRef.current) break
          const res = await getPurchasingReviewQueue({ status: segment, limit: 200, cursor })
          if (crawlGen !== reviewCrawlGenRef.current) break
          setQueueRows((prev) => dedupeReviewQueueRows([...prev, ...res.rows]))
          cursor = res.next_cursor ?? undefined
          crawlCursorRef.current = cursor ?? null
          setNextCursor(cursor ?? null)
        }
      } catch (err) {
        toast.error(formatApiError(err, 'Failed to load review queue'))
      } finally {
        if (crawlGen === reviewCrawlGenRef.current) {
          setBackgroundLoading(false)
          if (!cursor) crawlCursorRef.current = null
        }
      }
    },
    [segment],
  )

  const resumeCrawl = useCallback(
    async (fromCursor: string) => {
      const crawlGen = ++reviewCrawlGenRef.current
      await crawlBackgroundPages(crawlGen, fromCursor)
    },
    [crawlBackgroundPages],
  )

  const loadFirstPage = useCallback(async () => {
    const crawlGen = ++reviewCrawlGenRef.current
    crawlCursorRef.current = null
    setLoadingQueue(true)
    setBackgroundLoading(false)
    setQueueRows([])
    try {
      const first = await getPurchasingReviewQueue({ status: segment, limit: 200 })
      if (crawlGen !== reviewCrawlGenRef.current) return
      setQueueRows(dedupeReviewQueueRows(first.rows))
      const firstCursor = first.next_cursor
      crawlCursorRef.current = firstCursor
      setNextCursor(firstCursor)
      setSelectedRowKeys(new Set())
      lastSelectedIndexRef.current = null
      setLoadingQueue(false)

      if (!firstCursor) return
      await crawlBackgroundPages(crawlGen, firstCursor)
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to load review queue'))
    } finally {
      setLoadingQueue(false)
      setBackgroundLoading(false)
    }
  }, [crawlBackgroundPages, segment])

  useEffect(() => {
    void loadFirstPage()
  }, [loadFirstPage])

  useEffect(() => {
    if (segment !== 'needs_review') return
    if (debouncedReviewSearch.trim()) return
    if (loadingQueue || backgroundLoading) return
    if (crawlCursorRef.current) return
    usePurchasingNavStore.getState().setCount(dedupeReviewQueueRows(queueRows).length)
  }, [
    backgroundLoading,
    debouncedReviewSearch,
    loadingQueue,
    queueRows,
    segment,
  ])

  useEffect(() => {
    const query = debouncedReviewSearch.trim()
    if (!query) {
      const resumeCursor = crawlCursorRef.current
      if (resumeCursor) {
        void resumeCrawl(resumeCursor)
      }
      return
    }
    reviewCrawlGenRef.current += 1
    setBackgroundLoading(false)
    const gen = ++reviewSearchFetchGenRef.current
    let cancelled = false

    async function loadSearchMatches() {
      try {
        let cursor: string | undefined
        for (;;) {
          const res = await getPurchasingReviewQueue({
            status: segment,
            q: query,
            limit: 200,
            cursor,
          })
          if (cancelled || gen !== reviewSearchFetchGenRef.current) return
          setQueueRows((prev) => dedupeReviewQueueRows([...prev, ...res.rows]))
          if (!res.next_cursor) break
          cursor = res.next_cursor
        }
      } catch (err) {
        if (!cancelled && gen === reviewSearchFetchGenRef.current) {
          toast.error(formatApiError(err, 'Failed to search review queue'))
        }
      }
    }

    void loadSearchMatches()
    return () => {
      cancelled = true
    }
  }, [debouncedReviewSearch, resumeCrawl, segment])

  useEffect(() => {
    let cancelled = false
    void getPurchasingMetrics()
      .then((res) => {
        if (!cancelled) setMetricsTotals(res.totals)
      })
      .catch(() => {
        if (!cancelled) setMetricsTotals(null)
      })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    void searchPurchasingMeezIngredients({ limit: PURCHASING_MEEZ_CATALOG_LIMIT })
      .then((meezRes) => {
        if (!cancelled) setCatalogMeezIngredients(meezRes.rows)
      })
      .catch(() => {
        if (!cancelled) setCatalogMeezIngredients([])
      })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    async function loadCatalog() {
      const query = debouncedCatalogSearch.trim()
      const foldedQuery = query ? foldForSearch(query) : ''
      try {
        const productsRes = await listPurchasingProducts({ q: query || undefined, limit: 100 })
        if (cancelled) return
        const meezRows = foldedQuery
          ? catalogMeezIngredients.filter((row) => foldForSearch(row.name).includes(foldedQuery))
          : catalogMeezIngredients
        setFetchedCatalogOptions(
          mergeReviewSelectOptions(
            productsRes.rows.map(productRowToSelectOption),
            meezRows.map(meezRowToSelectOption),
          ),
        )
        setCatalogProducts(productsRes.rows)
      } catch {
        if (!cancelled) setFetchedCatalogOptions([])
      }
    }
    void loadCatalog()
    return () => {
      cancelled = true
    }
  }, [catalogMeezIngredients, debouncedCatalogSearch])

  const vendorOptions = useMemo(
    () => qboVendorFilterOptions(queueRows.map((row) => row.qbo_vendor_id), vendorNameMap),
    [queueRows, vendorNameMap],
  )

  const getSelectionForRow = useCallback(
    (row: PurchasingReviewQueueRow): ReviewSelection | null =>
      selectionByVendorItemId[row.vendor_item_id] ?? null,
    [selectionByVendorItemId],
  )

  const getCatalogOptionsForRow = useCallback(
    (row: PurchasingReviewQueueRow): SelectOption[] =>
      buildReviewCatalogOptionsForRow(
        row,
        fetchedCatalogOptions,
        createdProductOptions,
        catalogMeezIngredients,
      ),
    [catalogMeezIngredients, fetchedCatalogOptions, createdProductOptions],
  )

  const getOptionIdForRow = useCallback(
    (row: PurchasingReviewQueueRow): string | null => {
      const explicit = optionIdByVendorItemId[row.vendor_item_id]
      let optionId: string | null
      if (explicit !== undefined) optionId = explicit
      else if (row.product_id != null) optionId = `product:${row.product_id}`
      else if (row.meez_ingredient_id) optionId = `meez:${row.meez_ingredient_id}`
      else if (row.suggestion)
        optionId = defaultSuggestionOptionId(
          row.suggestion,
          row.vendor_item_code,
          catalogMeezIngredients,
        )
      else optionId = null
      return preferExistingCatalogOption(
        optionId,
        getCatalogOptionsForRow(row),
        row.vendor_item_code,
      )
    },
    [catalogMeezIngredients, getCatalogOptionsForRow, optionIdByVendorItemId],
  )

  const productLinkOptimisticContext = useCallback(
    (row: PurchasingReviewQueueRow): ProductLinkOptimisticContext => {
      const optionId = getOptionIdForRow(row)
      const optionLabel = getCatalogOptionsForRow(row).find((opt) => opt.id === optionId)?.label
      return {
        products: catalogProducts,
        meezRows: catalogMeezIngredients,
        optionLabel,
      }
    },
    [catalogMeezIngredients, catalogProducts, getCatalogOptionsForRow, getOptionIdForRow],
  )

  const reviewFilterContext = useMemo((): PurchasingReviewFilterContext => ({
    getOptionId: getOptionIdForRow,
    catalogOptionsForRow: getCatalogOptionsForRow,
    products: catalogProducts,
    meezRows: catalogMeezIngredients,
    meezNameRows: catalogMeezIngredients,
  }), [
    catalogMeezIngredients,
    catalogProducts,
    getCatalogOptionsForRow,
    getOptionIdForRow,
  ])

  const reasonOptions = useMemo(
    () =>
      distinctFieldOptions(
        collectReviewReasonFilterValuesForRows(queueRows, reviewFilterContext),
        reasonLabel,
      ),
    [queueRows, reviewFilterContext],
  )

  const filterFields = useMemo(
    () => buildPurchasingReviewFilterFields(vendorOptions, reasonOptions, reviewFilterContext),
    [vendorOptions, reasonOptions, reviewFilterContext],
  )

  const visibleColumnKeys = useMemo(() => {
    let keys = getVisibleFieldKeys(REVIEW_QUEUE_FIELD_DEFINITIONS, columnVisibility).filter(
      (key): key is ReviewQueueColumnKey =>
        (REVIEW_QUEUE_COLUMN_KEYS as readonly string[]).includes(key),
    )
    if (segment === 'done') {
      if (!keys.includes('approved')) {
        keys = [...keys, 'approved']
      }
    } else {
      keys = keys.filter((key) => key !== 'approved')
    }
    return keys
  }, [columnVisibility, segment])

  const filterPills = useMemo(
    () => generateFilterPills(filterTree, filterFields),
    [filterTree, filterFields],
  )
  const activeFilterCount = useMemo(() => countFilterConditions(filterTree), [filterTree])

  const filteredQueueRows = useMemo(() => {
    let rows = filterRowsWithTree(queueRows, filterTree, filterFields)
    rows = filterRowsByText(rows, textSearch, reviewQueueSearchText)
    return rows
  }, [queueRows, filterTree, filterFields, textSearch])

  const { rowSorts, groupCountSort } = useMemo(
    () => partitionReviewSortSpecs(sortSpecs),
    [sortSpecs],
  )

  const reviewSortFields = useMemo(() => buildReviewQueueSortFields(groupBy), [groupBy])

  const sortedQueueRows = useMemo(
    () => applySorts(filteredQueueRows, rowSorts, REVIEW_QUEUE_FIELD_DEFINITIONS),
    [filteredQueueRows, rowSorts],
  )

  const uniqueSortedQueueRows = useMemo(
    () => dedupeReviewQueueRows(sortedQueueRows),
    [sortedQueueRows],
  )

  const tableRows = useMemo(
    () => buildReviewTableRows(uniqueSortedQueueRows, groupBy, vendorNameMap, groupCountSort),
    [uniqueSortedQueueRows, groupBy, vendorNameMap, groupCountSort],
  )
  tableRowsRef.current = tableRows

  const navItems = useMemo(() => displayNavItems(tableRows), [tableRows])
  const itemIndexByTableRowKey = useMemo(
    () => buildItemIndexByTableRowKey(tableRows),
    [tableRows],
  )

  useEffect(() => {
    navItemsRef.current = navItems
  }, [navItems])

  const visibleRowKeys = useMemo(
    () => navItems.map((item) => item.tableRowKey),
    [navItems],
  )

  const scrollRef = useRef<HTMLDivElement>(null)
  const rowVirtualizerRef = useRef<{ scrollToIndex: (index: number, opts?: { align?: 'auto' }) => void } | null>(
    null,
  )

  const rowVirtualizer = useVirtualizer({
    count: tableRows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: (index) =>
      tableRows[index]?.kind === 'group' ? REVIEW_GROUP_ROW_HEIGHT : REVIEW_ITEM_ROW_HEIGHT,
    overscan: 6,
    getItemKey: (index) => {
      const entry = tableRows[index]
      if (!entry) return index
      if (entry.kind === 'group') return `group-${entry.label}`
      return `item-${entry.row.id}`
    },
  })
  rowVirtualizerRef.current = rowVirtualizer

  const scrollVirtualIndexIntoView = useCallback((virtualIndex: number) => {
    if (virtualIndex < 0) return
    rowVirtualizerRef.current?.scrollToIndex(virtualIndex, { align: 'auto' })
  }, [])

  const scrollNavIndexIntoView = useCallback(
    (navIndex: number) => {
      scrollVirtualIndexIntoView(virtualIndexForNavIndex(tableRowsRef.current, navIndex))
    },
    [scrollVirtualIndexIntoView],
  )

  const setCursorIndex = useCallback(
    (updater: number | null | ((prev: number | null) => number | null)) => {
      const newValue = typeof updater === 'function' ? updater(cursorIndexRef.current) : updater
      const changed = newValue !== cursorIndexRef.current
      cursorIndexRef.current = newValue
      setCursorIndexState(newValue)
      if (changed && newValue !== null) {
        scrollNavIndexIntoView(newValue)
      }
    },
    [scrollNavIndexIntoView],
  )

  useEffect(() => {
    const count = navItems.length
    if (count === 0) {
      setCursorIndex(null)
      return
    }
    setCursorIndex((prev) => clampFocusedRowIndex(prev ?? 0, count))
  }, [groupBy, navItems, setCursorIndex])

  const healthFirstSeen = metricsTotals?.first_seen_flag ?? 0
  const healthRepeat = metricsTotals?.repeat_flag ?? 0
  const hasHealthMetrics = metricsTotals != null && (healthFirstSeen > 0 || healthRepeat > 0)
  const showHealthNoData = metricsTotals != null && !hasHealthMetrics

  const headerCountLabel = useMemo(() => {
    if (loadingQueue && uniqueSortedQueueRows.length === 0) {
      return 'Loading…'
    }
    const lineCount = uniqueSortedQueueRows.length
    if (segment === 'done') {
      return `${lineCount.toLocaleString('en-US')} line${lineCount !== 1 ? 's' : ''} done`
    }
    const pendingCount = uniqueSortedQueueRows.filter((row) => !isAutoLinkedReviewRow(row)).length
    const autoCount = uniqueSortedQueueRows.filter((row) => isAutoLinkedReviewRow(row)).length
    const suffix = backgroundLoading ? '+' : ''
    return `${pendingCount.toLocaleString('en-US')}${suffix} pending · ${autoCount.toLocaleString('en-US')}${suffix} auto-linked`
  }, [backgroundLoading, loadingQueue, segment, uniqueSortedQueueRows])

  const adjustPurchasingNavCount = useCallback(
    (delta: number) => {
      if (segment === 'needs_review') {
        usePurchasingNavStore.getState().adjustCount(delta)
      }
    },
    [segment],
  )

  const applyMatchLocally = useCallback(
    (vendorItemId: number, patch: Partial<PurchasingReviewQueueRow>) => {
      setQueueRows((prev) => applyVendorItemMatchLocally(prev, vendorItemId, patch))
    },
    [],
  )

  const parseOptionSelection = useCallback(
    (row: PurchasingReviewQueueRow, optionId: string | null): ReviewSelection | null =>
      selectionFromProductOptionId(row, optionId),
    [],
  )

  const handleProductOptionChange = useCallback(
    (row: PurchasingReviewQueueRow, optionId: string | null) => {
      setOptionIdByVendorItemId((prev) => ({ ...prev, [row.vendor_item_id]: optionId }))
      setSelectionByVendorItemId((prev) => ({
        ...prev,
        [row.vendor_item_id]: selectionFromProductOptionId(row, optionId),
      }))
    },
    [],
  )

  const applyMatchResultLocally = useCallback(
    (
      row: PurchasingReviewQueueRow,
      result: PurchasingVendorItemMatchResponse,
      productAtomicUom: string | null,
    ) => {
      const resolvedAtomicUom = result.product_atomic_uom ?? productAtomicUom
      const reviewFlags = result.uom_conflict
        ? [...new Set([...(row.review_flags ?? []), 'uom_conflict'])]
        : (row.review_flags ?? []).filter((flag) => flag !== 'uom_conflict')
      const patch: Partial<PurchasingReviewQueueRow> = {
        product_id: result.product_id,
        product_name: result.product_name ?? row.product_name,
        match_status: result.match_status,
        product_atomic_uom: resolvedAtomicUom,
        review_flags: reviewFlags,
      }
      setQueueRows((prev) => {
        const next = applyVendorItemMatchLocally(prev, row.vendor_item_id, patch)
        if (segment !== 'needs_review') return next
        return dropVendorItemRowsLeavingNeedsReview(next, row.vendor_item_id)
      })
      if (result.product_id != null && (result.product_name ?? row.product_name)) {
        setCreatedProductOptions((prev) =>
          mergeReviewSelectOptions(prev, [
            productRowToSelectOption({
              id: result.product_id,
              name: result.product_name ?? row.product_name ?? '',
              atomic_uom: resolvedAtomicUom ?? '',
            }),
          ]),
        )
      }
      setOptionIdByVendorItemId((prev) => {
        const next = { ...prev }
        delete next[row.vendor_item_id]
        return next
      })
      setSelectionByVendorItemId((prev) => {
        const next = { ...prev }
        delete next[row.vendor_item_id]
        return next
      })
      setModalRow((prev) => {
        if (!prev || prev.vendor_item_id !== row.vendor_item_id) return prev
        const merged = { ...prev, ...patch, reasons: buildReasonsForQueueRow({ ...prev, ...patch }) }
        if (segment === 'needs_review' && !rowBelongsOnNeedsReview(merged)) return null
        return merged
      })
    },
    [segment],
  )

  const nextProductLinkEpoch = useCallback((vendorItemId: number) => {
    const next = (productLinkEpochByVendorItemRef.current.get(vendorItemId) ?? 0) + 1
    productLinkEpochByVendorItemRef.current.set(vendorItemId, next)
    return next
  }, [])

  const currentProductLinkEpoch = useCallback(
    (vendorItemId: number) => productLinkEpochByVendorItemRef.current.get(vendorItemId) ?? 0,
    [],
  )

  const performProductLinkUndo = useCallback(
    (
      vendorItemId: number,
      snapshots: PurchasingReviewQueueRow[],
      originalIndices: number[],
      removedCount: number,
      optimisticPatch: Partial<PurchasingReviewQueueRow>,
      toastId?: string,
    ) => {
      const undoEpoch = nextProductLinkEpoch(vendorItemId)
      setQueueRows((prev) => restoreLinesToQueue(prev, snapshots, originalIndices))
      if (removedCount > 0) {
        adjustPurchasingNavCount(removedCount)
      }
      setModalRow((prev) =>
        prev && prev.vendor_item_id === vendorItemId ? snapshots[0] ?? prev : prev,
      )
      if (toastId) {
        dismissToast(toastId)
      }
      void (async () => {
        const pending = matchInFlightByVendorItemRef.current.get(vendorItemId)
        if (pending) await pending
        if (shouldSkipProductLinkPersistApply(undoEpoch, currentProductLinkEpoch(vendorItemId))) {
          return
        }
        try {
          await unlinkPurchasingVendorItem(vendorItemId)
        } catch (undoErr) {
          if (isVendorItemAlreadyUnlinkedError(undoErr)) return
          if (shouldSkipProductLinkPersistApply(undoEpoch, currentProductLinkEpoch(vendorItemId))) {
            return
          }
          setQueueRows((prev) =>
            applyOptimisticProductLinkToQueue(prev, vendorItemId, optimisticPatch, segment).rows,
          )
          if (removedCount > 0) {
            adjustPurchasingNavCount(-removedCount)
          }
          toast.error(formatApiError(undoErr, 'Undo failed'))
        }
      })()
    },
    [adjustPurchasingNavCount, currentProductLinkEpoch, nextProductLinkEpoch, segment],
  )

  const completeProductLink = useCallback(
    (
      row: PurchasingReviewQueueRow,
      result: PurchasingVendorItemMatchResponse,
      productAtomicUom: string | null,
      payload: PurchasingVendorItemMatchRequest,
    ) => {
      const vendorItemId = row.vendor_item_id
      const { snapshots, originalIndices } = collectVendorItemSnapshotPlacements(
        queueRows,
        vendorItemId,
      )
      const optimisticPatch = buildOptimisticProductLinkPatch(
        row,
        payload,
        productLinkOptimisticContext(row),
      )
      const { removedLineIds } = applyOptimisticProductLinkToQueue(
        queueRows,
        vendorItemId,
        optimisticPatch,
        segment,
      )
      const removedCount = removedLineIds.length
      applyMatchResultLocally(row, result, productAtomicUom)
      if (result.uom_conflict) {
        toast.warning(uomConflictToastFromMatch(result, row, productAtomicUom))
      } else if (removedCount > 0) {
        const toastId = toast.success('Product linked', {
          action: {
            label: 'Undo',
            onClick: () => {
              performProductLinkUndo(
                vendorItemId,
                snapshots,
                originalIndices,
                removedCount,
                optimisticPatch,
                toastId,
              )
            },
          },
        })
      }
    },
    [
      applyMatchResultLocally,
      performProductLinkUndo,
      productLinkOptimisticContext,
      queueRows,
      segment,
    ],
  )

  const confirmProductLinkWithPayload = useCallback(
    async (row: PurchasingReviewQueueRow, payload: PurchasingVendorItemMatchRequest) => {
      const vendorItemId = row.vendor_item_id
      const persistEpoch = nextProductLinkEpoch(vendorItemId)
      const { snapshots, originalIndices } = collectVendorItemSnapshotPlacements(
        queueRows,
        vendorItemId,
      )
      const optimisticPatch = buildOptimisticProductLinkPatch(
        row,
        payload,
        productLinkOptimisticContext(row),
      )
      const predictedUomConflict = uomsConflict(row.atomic_uom, optimisticPatch.product_atomic_uom)
      const { removedLineIds } = applyOptimisticProductLinkToQueue(
        queueRows,
        vendorItemId,
        optimisticPatch,
        segment,
      )
      const removedCount = removedLineIds.length

      setVendorItemBusy(vendorItemId, true)
      setQueueRows((prev) =>
        applyOptimisticProductLinkToQueue(prev, vendorItemId, optimisticPatch, segment).rows,
      )
      if (removedCount > 0) {
        adjustPurchasingNavCount(-removedCount)
        setModalRow((prev) => (prev && prev.vendor_item_id === vendorItemId ? null : prev))
      }

      const persist = async () => {
        try {
          const result = await matchPurchasingVendorItem(vendorItemId, payload)
          const productAtomicUom =
            result.product_atomic_uom ?? productAtomicUomFromCatalog(result.product_id, catalogProducts)
          if (shouldSkipProductLinkPersistApply(persistEpoch, currentProductLinkEpoch(vendorItemId))) {
            return
          }
          if (result.uom_conflict && !predictedUomConflict) {
            const restoredRows = snapshots.map(
              (snapshotRow) =>
                applyVendorItemMatchLocally([snapshotRow], vendorItemId, {
                  product_id: result.product_id,
                  product_name: result.product_name ?? snapshotRow.product_name,
                  match_status: result.match_status,
                  product_atomic_uom: productAtomicUom,
                  review_flags: [...new Set([...(snapshotRow.review_flags ?? []), 'uom_conflict'])],
                })[0]!,
            )
            setQueueRows((prev) =>
              restoreLinesToQueue(prev, restoredRows, originalIndices),
            )
            if (removedCount > 0) {
              adjustPurchasingNavCount(removedCount)
            }
            toast.warning(uomConflictToastFromMatch(result, row, productAtomicUom))
            return
          }
          if (shouldSkipProductLinkPersistApply(persistEpoch, currentProductLinkEpoch(vendorItemId))) {
            return
          }
          applyMatchResultLocally(row, result, productAtomicUom)
          if (result.uom_conflict) {
            toast.warning(uomConflictToastFromMatch(result, row, productAtomicUom))
          }
        } catch (err) {
          if (shouldSkipProductLinkPersistApply(persistEpoch, currentProductLinkEpoch(vendorItemId))) {
            return
          }
          const candidates = parseDuplicateCandidates(err)
          setQueueRows((prev) => restoreLinesToQueue(prev, snapshots, originalIndices))
          if (removedCount > 0) {
            adjustPurchasingNavCount(removedCount)
          }
          if (candidates) {
            setDuplicateConflict({ kind: 'match', row, payload, candidates })
            return
          }
          toast.error(formatApiError(err, 'Failed to link product'))
        } finally {
          setVendorItemBusy(vendorItemId, false)
        }
      }

      const inFlight = persist()
      matchInFlightByVendorItemRef.current.set(
        vendorItemId,
        inFlight.then(() => undefined),
      )
      if (removedCount > 0 && !predictedUomConflict) {
        const toastId = toast.success('Product linked', {
          action: {
            label: 'Undo',
            onClick: () => {
              performProductLinkUndo(
                vendorItemId,
                snapshots,
                originalIndices,
                removedCount,
                optimisticPatch,
                toastId,
              )
            },
          },
        })
      }
      await inFlight
      matchInFlightByVendorItemRef.current.delete(vendorItemId)
    },
    [
      adjustPurchasingNavCount,
      applyMatchResultLocally,
      catalogProducts,
      currentProductLinkEpoch,
      nextProductLinkEpoch,
      performProductLinkUndo,
      productLinkOptimisticContext,
      queueRows,
      segment,
      setVendorItemBusy,
    ],
  )

  const confirmProductLink = useCallback(
    (row: PurchasingReviewQueueRow, explicitOptionId?: string | null) => {
      const optionId = explicitOptionId ?? getOptionIdForRow(row)
      const selection = selectionFromProductOptionId(row, optionId)
      const payload = buildMatchRequestFromRow(row, selection)
      if (!payload) {
        toast.error('Choose a product to link')
        return
      }
      void confirmProductLinkWithPayload(row, payload)
    },
    [confirmProductLinkWithPayload, getOptionIdForRow],
  )

  const applyCreateAndMatchLocally = useCallback(
    (
      row: PurchasingReviewQueueRow,
      created: { id: number; name: string; atomic_uom: string },
      matchResult: PurchasingVendorItemMatchResponse,
    ) => {
      applyMatchLocally(row.vendor_item_id, {
        product_id: created.id,
        product_name: created.name,
        product_atomic_uom: created.atomic_uom,
        match_status: 'confirmed',
        review_flags: matchResult.uom_conflict
          ? [...new Set([...(row.review_flags ?? []), 'uom_conflict'])]
          : (row.review_flags ?? []).filter((flag) => flag !== 'uom_conflict'),
      })
      if (matchResult.uom_conflict) {
        toast.warning(uomConflictToastFromMatch(matchResult, row, created.atomic_uom))
      } else {
        toast.success('Product created and matched')
      }
    },
    [applyMatchLocally],
  )

  const handleDuplicateUseExisting = useCallback(
    async (productId: number) => {
      if (!duplicateConflict) return
      const { row } = duplicateConflict
      setDuplicateConflictBusy(true)
      try {
        const result = await matchPurchasingVendorItem(row.vendor_item_id, { product_id: productId })
        const productAtomicUom = productAtomicUomFromCatalog(result.product_id, catalogProducts)
        const productName =
          duplicateConflict.candidates.find((candidate) => candidate.id === productId)?.name ?? null
        setOptionIdByVendorItemId((prev) => ({
          ...prev,
          [row.vendor_item_id]: `product:${productId}`,
        }))
        setSelectionByVendorItemId((prev) => ({
          ...prev,
          [row.vendor_item_id]: { kind: 'product', productId },
        }))
        if (duplicateConflict.kind === 'match') {
          completeProductLink(row, result, productAtomicUom, { product_id: productId })
        } else {
          applyMatchLocally(row.vendor_item_id, {
            product_id: productId,
            product_name: productName,
            product_atomic_uom: productAtomicUom,
            match_status: 'confirmed',
            review_flags: result.uom_conflict
              ? [...new Set([...(row.review_flags ?? []), 'uom_conflict'])]
              : (row.review_flags ?? []).filter((flag) => flag !== 'uom_conflict'),
          })
          if (result.uom_conflict) {
            toast.warning(uomConflictToastFromMatch(result, row, productAtomicUom))
          } else {
            toast.success('Product linked')
          }
          setCreateProductRow(null)
        }
        setDuplicateConflict(null)
      } catch (err) {
        toast.error(formatApiError(err, 'Failed to link product'))
      } finally {
        setDuplicateConflictBusy(false)
      }
    },
    [applyMatchLocally, catalogProducts, completeProductLink, duplicateConflict],
  )

  const handleDuplicateCreateAnyway = useCallback(async () => {
    if (!duplicateConflict) return
    const { row } = duplicateConflict
    setDuplicateConflictBusy(true)
    try {
      if (duplicateConflict.kind === 'match') {
        const result = await matchPurchasingVendorItem(row.vendor_item_id, {
          ...duplicateConflict.payload,
          force: true,
        })
        const productAtomicUom = productAtomicUomFromCatalog(result.product_id, catalogProducts)
        completeProductLink(row, result, productAtomicUom, duplicateConflict.payload)
      } else {
        const created = await createPurchasingProduct({ ...duplicateConflict.payload, force: true })
        const option: SelectOption = {
          id: `product:${created.id}`,
          label: created.name,
          group: 'Products',
        }
        setCreatedProductOptions((prev) => [option, ...prev])
        const matchResult = await matchPurchasingVendorItem(row.vendor_item_id, {
          product_id: created.id,
        })
        applyCreateAndMatchLocally(row, created, matchResult)
        setCreateProductRow(null)
      }
      setDuplicateConflict(null)
    } catch (err) {
      toast.error(
        formatApiError(
          err,
          duplicateConflict.kind === 'match' ? 'Failed to link product' : 'Failed to create product',
        ),
      )
    } finally {
      setDuplicateConflictBusy(false)
    }
  }, [
    applyCreateAndMatchLocally,
    catalogProducts,
    completeProductLink,
    duplicateConflict,
  ])

  const clearProductSelection = useCallback(
    async (row: PurchasingReviewQueueRow) => {
      const optionId = getOptionIdForRow(row)
      if (!canClearProductSelection(optionId)) return
      setVendorItemBusy(row.vendor_item_id, true)
      try {
        if (rowHasPersistedProductLink(row)) {
          await unlinkPurchasingVendorItem(row.vendor_item_id)
          setQueueRows((prev) => applyVendorItemUnlinkLocally(prev, row.vendor_item_id))
          setModalRow((prev) =>
            prev && prev.vendor_item_id === row.vendor_item_id
              ? applyVendorItemUnlinkLocally([prev], row.vendor_item_id)[0]!
              : prev,
          )
        }
        setOptionIdByVendorItemId((prev) => ({ ...prev, [row.vendor_item_id]: null }))
        setSelectionByVendorItemId((prev) => ({ ...prev, [row.vendor_item_id]: null }))
      } catch (err) {
        toast.error(formatApiError(err, 'Failed to clear product link'))
      } finally {
        setVendorItemBusy(row.vendor_item_id, false)
      }
    },
    [getOptionIdForRow, setVendorItemBusy],
  )

  const handleBulkConfirmLink = useCallback(async () => {
    const rows = queueRows.filter((row) => selectedLineIds.has(row.id))
    const linkable = rows.filter((row) => canConfirmProductLink(row, getOptionIdForRow(row)))
    if (linkable.length === 0) {
      toast.error('Choose a product to link for each selected row')
      return
    }
    if (linkable.length < rows.length) {
      toast.error('Some selected rows have no product selected')
      return
    }

    const vendorItemIds = [...new Set(linkable.map((row) => row.vendor_item_id))]
    const snapshotsByVendorItem = new Map<number, PurchasingReviewQueueRow[]>()
    for (const entry of queueRows) {
      if (!vendorItemIds.includes(entry.vendor_item_id)) continue
      const list = snapshotsByVendorItem.get(entry.vendor_item_id) ?? []
      list.push(entry)
      snapshotsByVendorItem.set(entry.vendor_item_id, list)
    }
    const representativeByVendorItem = new Map(
      linkable.map((row) => [row.vendor_item_id, row] as const),
    )

    setVendorItemsBusy(vendorItemIds, true)
    const removedByVendorItem = new Map<number, number>()
    let preview = queueRows
    for (const vendorItemId of vendorItemIds) {
      const rep = representativeByVendorItem.get(vendorItemId)
      if (!rep) continue
      const selection = selectionFromProductOptionId(rep, getOptionIdForRow(rep))
      const payload = buildMatchRequestFromRow(rep, selection)
      if (!payload) continue
      const patch = buildOptimisticProductLinkPatch(rep, payload, productLinkOptimisticContext(rep))
      const applied = applyOptimisticProductLinkToQueue(preview, vendorItemId, patch, segment)
      preview = applied.rows
      removedByVendorItem.set(vendorItemId, applied.removedLineIds.length)
    }
    const totalRemoved = [...removedByVendorItem.values()].reduce((sum, count) => sum + count, 0)
    setQueueRows((prev) => {
      let next = prev
      for (const vendorItemId of vendorItemIds) {
        const rep = representativeByVendorItem.get(vendorItemId)
        if (!rep) continue
        const selection = selectionFromProductOptionId(rep, getOptionIdForRow(rep))
        const payload = buildMatchRequestFromRow(rep, selection)
        if (!payload) continue
        const patch = buildOptimisticProductLinkPatch(
          rep,
          payload,
          productLinkOptimisticContext(rep),
        )
        next = applyOptimisticProductLinkToQueue(next, vendorItemId, patch, segment).rows
      }
      return next
    })
    if (totalRemoved > 0) {
      adjustPurchasingNavCount(-totalRemoved)
    }
    setSelectedRowKeys(new Set())

    try {
      const results = await Promise.all(
        vendorItemIds.map(async (vendorItemId) => {
          const rep = representativeByVendorItem.get(vendorItemId)
          if (!rep) return { vendorItemId, ok: false as const }
          const snapshots = snapshotsByVendorItem.get(vendorItemId) ?? []
          const selection = selectionFromProductOptionId(rep, getOptionIdForRow(rep))
          const payload = buildMatchRequestFromRow(rep, selection)
          if (!payload) return { vendorItemId, ok: false as const }
          const optimisticPatch = buildOptimisticProductLinkPatch(
            rep,
            payload,
            productLinkOptimisticContext(rep),
          )
          const predictedUomConflict = uomsConflict(rep.atomic_uom, optimisticPatch.product_atomic_uom)
          try {
            const result = await matchPurchasingVendorItem(vendorItemId, payload)
            const productAtomicUom = productAtomicUomFromCatalog(result.product_id, catalogProducts)
            if (result.uom_conflict && !predictedUomConflict) {
              const restoredRows = snapshots.map(
                (snapshotRow) =>
                  applyVendorItemMatchLocally([snapshotRow], vendorItemId, {
                    product_id: result.product_id,
                    product_name: result.product_name ?? snapshotRow.product_name,
                    match_status: result.match_status,
                    product_atomic_uom: productAtomicUom,
                    review_flags: [
                      ...new Set([...(snapshotRow.review_flags ?? []), 'uom_conflict']),
                    ],
                  })[0]!,
              )
              setQueueRows((prev) => restoreLinesToQueue(prev, restoredRows))
              const removed = removedByVendorItem.get(vendorItemId) ?? 0
              if (removed > 0) adjustPurchasingNavCount(removed)
              toast.warning(uomConflictToastFromMatch(result, rep, productAtomicUom))
              return { vendorItemId, ok: false as const }
            }
            applyMatchResultLocally(rep, result, productAtomicUom)
            if (result.uom_conflict) {
              toast.warning(uomConflictToastFromMatch(result, rep, productAtomicUom))
            }
            return { vendorItemId, ok: true as const }
          } catch (err) {
            setQueueRows((prev) => restoreLinesToQueue(prev, snapshots))
            const removed = removedByVendorItem.get(vendorItemId) ?? 0
            if (removed > 0) adjustPurchasingNavCount(removed)
            toast.error(formatApiError(err, `Failed to link vendor item ${vendorItemId}`))
            return { vendorItemId, ok: false as const }
          }
        }),
      )
      const succeeded = results.filter((entry) => entry.ok).length
      if (succeeded > 0) {
        toast.success(`Linked ${succeeded} vendor item(s)`)
      }
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to bulk link'))
    } finally {
      setVendorItemsBusy(vendorItemIds, false)
    }
  }, [
    adjustPurchasingNavCount,
    applyMatchResultLocally,
    catalogProducts,
    getOptionIdForRow,
    productLinkOptimisticContext,
    queueRows,
    segment,
    selectedLineIds,
    setVendorItemsBusy,
  ])

  const handleApproveLines = useCallback(
    async (lineIds: number[]) => {
      const rows = queueRows.filter(
        (row) =>
          lineIds.includes(row.id) &&
          !approvingLineIdsRef.current.has(row.id) &&
          rowIsBulkApprovable(row, getSelectionForRow(row), catalogProducts, catalogMeezIngredients),
      )
      if (rows.length === 0) return

      const snapshot = rows
      const snapshotIds = new Set(snapshot.map((r) => r.id))
      const uomConflictRows = new Map<number, PurchasingReviewQueueRow>()
      for (const id of snapshotIds) approvingLineIdsRef.current.add(id)

      setQueueRows((prev) => removeLinesFromQueue(prev, snapshotIds))
      adjustPurchasingNavCount(-snapshot.length)
      setSelectedRowKeys((prev) => {
        const next = new Set(prev)
        for (const key of prev) {
          const lineId = Number(key.split('\0')[1])
          if (snapshotIds.has(lineId)) next.delete(key)
        }
        return next
      })
      if (modalRow && snapshotIds.has(modalRow.id)) setModalRow(null)

      try {
        const matchFailedIds = new Set<number>()
        const matchRows = snapshot.filter((row) => vendorItemNeedsProduct(row))

        const matchResults = await Promise.all(
          matchRows.map(async (row) => {
            const payload = buildMatchRequestFromRow(row, getSelectionForRow(row))
            if (!payload) {
              return { row, ok: false as const }
            }
            try {
              const result = await matchPurchasingVendorItem(row.vendor_item_id, payload)
              if (result.uom_conflict) {
                const productAtomicUom = productAtomicUomFromCatalog(result.product_id, catalogProducts)
                const updated = applyVendorItemMatchLocally([row], row.vendor_item_id, {
                  product_id: result.product_id,
                  match_status: result.match_status,
                  product_atomic_uom: productAtomicUom,
                  review_flags: [...new Set([...(row.review_flags ?? []), 'uom_conflict'])],
                })[0]!
                uomConflictRows.set(row.id, updated)
                toast.warning(uomConflictToastFromMatch(result, row, productAtomicUom))
                return { row, ok: false as const }
              }
              return { row, ok: true as const }
            } catch (err) {
              toast.error(formatApiError(err, 'Approve failed — could not link product'))
              return { row, ok: false as const }
            }
          }),
        )

        for (const result of matchResults) {
          if (!result.ok) matchFailedIds.add(result.row.id)
        }

        if (matchFailedIds.size > 0) {
          const failedRows = snapshot
            .filter((r) => matchFailedIds.has(r.id))
            .map((r) => uomConflictRows.get(r.id) ?? r)
          setQueueRows((prev) => restoreLinesToQueue(prev, failedRows))
          adjustPurchasingNavCount(failedRows.length)
        }

        const toConfirm = snapshot.filter((r) => !matchFailedIds.has(r.id))
        if (toConfirm.length === 0) return

        try {
          const res = await patchPurchasingPurchaseLinesBulk(
            toConfirm.map((row) => ({ item_id: row.id, fields: { review_status: 'confirmed' } })),
          )
          const failed = res.results.filter((r) => !r.ok)
          if (failed.length > 0) {
            const failedIds = new Set(failed.map((f) => f.id))
            const failedRows = snapshot.filter((r) => failedIds.has(r.id))
            setQueueRows((prev) => restoreLinesToQueue(prev, failedRows))
            adjustPurchasingNavCount(failedRows.length)
            toast.error(`Approve failed for lines: ${failed.map((f) => f.id).join(', ')}`)
          } else {
            toast.success(`Approved ${toConfirm.length} line(s)`, {
              action: {
                label: 'Undo',
                onClick: () => {
                  setQueueRows((prev) => restoreLinesToQueue(prev, toConfirm))
                  adjustPurchasingNavCount(toConfirm.length)
                  void patchPurchasingPurchaseLinesBulk(
                    toConfirm.map((row) => ({
                      item_id: row.id,
                      fields: { review_status: 'flagged' },
                    })),
                  ).catch((err) => {
                    toast.error(formatApiError(err, 'Undo failed'))
                  })
                },
              },
            })
          }
        } catch (err) {
          setQueueRows((prev) => restoreLinesToQueue(prev, toConfirm))
          adjustPurchasingNavCount(toConfirm.length)
          toast.error(formatApiError(err, 'Approve failed'))
        }
      } finally {
        for (const id of snapshotIds) approvingLineIdsRef.current.delete(id)
      }
    },
    [
      adjustPurchasingNavCount,
      catalogMeezIngredients,
      catalogProducts,
      getSelectionForRow,
      modalRow,
      queueRows,
    ],
  )

  const handleBulkLink = useCallback(
    async (row: PurchasingReviewQueueRow, optionId: string | null) => {
      const selection = parseOptionSelection(row, optionId)
      const payload = buildMatchRequestFromRow(row, selection)
      if (!payload) {
        toast.error('Choose a product to link')
        return
      }
      const vendorItemIds = [...new Set(
        queueRows.filter((r) => selectedLineIds.has(r.id)).map((r) => r.vendor_item_id),
      )]
      try {
        const res = await matchPurchasingBulk({ vendor_item_ids: vendorItemIds, ...payload })
        const failed = res.results.filter(
          (r): r is Record<string, unknown> =>
            typeof r === 'object' && r != null && (r as { ok?: boolean }).ok === false,
        )
        if (failed.length > 0) {
          const failedIds = failed
            .map((r) => r.vendor_item_id)
            .filter((id): id is number => typeof id === 'number')
          toast.error(
            failedIds.length > 0
              ? `Link failed for vendor items: ${failedIds.join(', ')}`
              : 'Some links failed',
          )
        } else if (
          !res.results.some(
            (entry) =>
              typeof entry === 'object' &&
              entry != null &&
              (entry as PurchasingVendorItemMatchResponse).uom_conflict,
          )
        ) {
          toast.success(`Linked ${vendorItemIds.length} vendor item(s)`, {
            action: {
              label: 'Undo',
              onClick: () => {
                void Promise.all(vendorItemIds.map((id) => unlinkPurchasingVendorItem(id))).then(() =>
                  void loadFirstPage(),
                )
              },
            },
          })
        }
        for (const vendorItemId of vendorItemIds) {
          if (failed.some((entry) => entry.vendor_item_id === vendorItemId)) continue
          const matchResult = res.results.find(
            (entry): entry is PurchasingVendorItemMatchResponse =>
              typeof entry === 'object' &&
              entry != null &&
              (entry as PurchasingVendorItemMatchResponse).vendor_item_id === vendorItemId,
          )
          const linkedRow = queueRows.find((entry) => entry.vendor_item_id === vendorItemId) ?? row
          const productId = matchResult?.product_id ?? payload.product_id ?? linkedRow.product_id
          const productAtomicUom =
            productId != null ? productAtomicUomFromCatalog(productId, catalogProducts) : null
          applyMatchLocally(vendorItemId, {
            product_id: productId ?? undefined,
            product_name: linkedRow.product_name,
            product_atomic_uom: productAtomicUom,
            match_status: 'confirmed',
            review_flags: matchResult?.uom_conflict
              ? [...new Set([...(linkedRow.review_flags ?? []), 'uom_conflict'])]
              : (linkedRow.review_flags ?? []).filter((flag) => flag !== 'uom_conflict'),
          })
          if (matchResult?.uom_conflict) {
            toast.warning(uomConflictToastFromMatch(matchResult, linkedRow, productAtomicUom))
          }
        }
        setBulkLinkRow(null)
        setSelectedRowKeys(new Set())
      } catch (err) {
        toast.error(formatApiError(err, 'Failed to bulk link'))
      }
    },
    [applyMatchLocally, catalogProducts, loadFirstPage, parseOptionSelection, queueRows, selectedLineIds],
  )

  const handleBulkEditSubmit = useCallback(async () => {
    const ids = [...selectedLineIds]
    if (ids.length === 0) return
    const fields: Record<string, string> = {}
    if (bulkEditQty.trim()) fields.quantity = bulkEditQty.trim()
    if (bulkEditUnitPrice.trim()) fields.unit_price = bulkEditUnitPrice.trim()
    if (bulkEditPack.trim()) fields.pack_text = bulkEditPack.trim()
    if (Object.keys(fields).length === 0) {
      toast.error('Enter at least one field to edit')
      return
    }
    try {
      const res = await patchPurchasingPurchaseLinesBulk(
        ids.map((id) => ({ item_id: id, fields })),
      )
      const failed = res.results.filter((r) => !r.ok)
      if (failed.length > 0) {
        toast.error(`Edit failed for lines: ${failed.map((f) => f.id).join(', ')}`)
      } else {
        toast.success(`Updated ${ids.length} line(s)`)
        setQueueRows((prev) =>
          prev.map((row) => {
            const result = res.results.find((r) => r.id === row.id && r.ok)
            if (!result) return row
            return applyLinePatchLocally([row], row.id, {
              quantity: result.quantity ?? row.quantity,
              unit_price: result.unit_price ?? row.unit_price,
              extended_price: result.extended_price ?? row.extended_price,
              cost_per_atomic_unit: result.cost_per_atomic_unit ?? row.cost_per_atomic_unit,
              pack_text: bulkEditPack.trim() || row.pack_text,
            })[0]!
          }),
        )
      }
      setShowBulkEdit(false)
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to bulk edit'))
    }
  }, [bulkEditPack, bulkEditQty, bulkEditUnitPrice, selectedLineIds])

  const openPackEditor = useCallback(
    (row: PurchasingReviewQueueRow, vendorItemIds?: number[]) => {
      setPackEditorTarget({
        vendorItemIds: vendorItemIds ?? [row.vendor_item_id],
        initialPackText: row.pack_text,
      })
    },
    [],
  )

  const openPackEditorForSelection = useCallback(() => {
    const rows = queueRows.filter((row) => {
      if (!selectedLineIds.has(row.id)) return false
      const reasons = queueRowReasons(
        row,
        getSelectionForRow(row),
        catalogProducts,
        catalogMeezIngredients,
      )
      return reasons.includes('missing_atomic_cost') || reasons.includes('uom_conflict')
    })
    const vendorItemIds = [...new Set(rows.map((row) => row.vendor_item_id))]
    if (vendorItemIds.length === 0) return
    openPackEditor(rows[0]!, vendorItemIds)
  }, [catalogMeezIngredients, catalogProducts, getSelectionForRow, openPackEditor, queueRows, selectedLineIds])

  const handlePackSaved = useCallback(
    (payload: { vendorItemIds: number[]; packText: string; recomputedLineCount: number }) => {
      setQueueRows((prev) =>
        applyPackSavedLocally(prev, payload.vendorItemIds, payload.packText),
      )
      toast.success(`Pack saved · recomputed ${payload.recomputedLineCount} line(s)`)
      setSelectedRowKeys(new Set())
      void loadFirstPage()
    },
    [loadFirstPage],
  )

  const handleUndoDone = useCallback(
    async (row: PurchasingReviewQueueRow) => {
      const hadProduct =
        row.product_id != null || row.match_status === 'auto' || row.match_status === 'confirmed'
      try {
        if (hadProduct) {
          await unlinkPurchasingVendorItem(row.vendor_item_id)
        }
        await patchPurchasingPurchaseLine(row.id, { review_status: 'flagged' })
        setQueueRows((prev) => applyUndoDoneLocally(prev, row.id, row.vendor_item_id, hadProduct))
        if (modalRow?.id === row.id) setModalRow(null)
        toast.success('Returned to Needs review')
      } catch (err) {
        toast.error(formatApiError(err, 'Failed to undo'))
      }
    },
    [modalRow],
  )

  const openModalForRow = useCallback(
    async (row: PurchasingReviewQueueRow) => {
      setModalRow(row)
      setModalDecidedBy(row.decided_by ?? null)
      setModalDecidedAt(row.decided_at ?? null)
      if (!row.decided_by && segment === 'done') {
        try {
          const events = await listPurchasingMatchEvents({ vendor_item_id: row.vendor_item_id })
          const decided = events.rows.find((event) => event.decided_by)
          if (decided) {
            setModalDecidedBy(decided.decided_by)
            setModalDecidedAt(decided.decided_at)
          }
        } catch {
          // optional enrichment
        }
      }
    },
    [segment],
  )

  const getCursorRow = useCallback((): PurchasingReviewQueueRow | null => {
    if (cursorIndex == null) return null
    return navItems[cursorIndex]?.row ?? null
  }, [cursorIndex, navItems])

  const toggleRowSelection = useCallback(
    (
      tableRowKeyValue: string,
      checked: boolean,
      event?: ReactMouseEvent,
      navIndex?: number,
    ) => {
      const currentIndex = navIndex ?? itemIndexByTableRowKey.get(tableRowKeyValue) ?? -1

      if (event?.shiftKey && lastSelectedIndexRef.current !== null && currentIndex !== -1) {
        const start = Math.min(lastSelectedIndexRef.current, currentIndex)
        const end = Math.max(lastSelectedIndexRef.current, currentIndex)
        setSelectedRowKeys((prev) => {
          const next = new Set(prev)
          for (let index = start; index <= end; index += 1) {
            const item = navItems[index]
            if (item) next.add(item.tableRowKey)
          }
          return next
        })
        setCursorIndex(currentIndex)
        return
      }

      setSelectedRowKeys((prev) => {
        const next = new Set(prev)
        if (checked) {
          next.add(tableRowKeyValue)
          if (currentIndex !== -1) lastSelectedIndexRef.current = currentIndex
        } else {
          next.delete(tableRowKeyValue)
        }
        return next
      })
      if (currentIndex !== -1) {
        setCursorIndex(currentIndex)
      }
    },
    [itemIndexByTableRowKey, navItems, setCursorIndex],
  )

  const renderProductPicker = useCallback(
    (
      row: PurchasingReviewQueueRow,
      idPrefix: string,
      options?: { useViewportForPlacement?: boolean },
    ) => {
      const optionId = getOptionIdForRow(row)
      const confirmState = productLinkConfirmButtonState(row, optionId)
      const busy = isVendorItemBusy(row.vendor_item_id)
      return (
        <div className="flex min-w-0 flex-col items-start gap-1">
          <div className="w-full min-w-0">
            <SearchableSelect
              id={`${idPrefix}-product-${row.id}`}
              options={getCatalogOptionsForRow(row)}
              value={optionId}
              onChange={(id) => handleProductOptionChange(row, id)}
              placeholder="Link product…"
              showDetailsInTrigger
              allowCreate
              disabled={busy}
              onCreateNew={(name) => {
                setCreateProductRow(row)
                setCreateProductName(name)
                return Promise.resolve(null)
              }}
              createLabel="Create product"
              size="compact"
              preserveOrder
              onSearchChange={setCatalogSearch}
              useViewportForPlacement={options?.useViewportForPlacement}
              preferBelow={options?.useViewportForPlacement}
            />
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="primary"
              size="sm"
              disabled={busy || confirmState.confirmDisabled}
              onClick={() => void confirmProductLink(row, optionId)}
            >
              {busy ? confirmState.busyLabel : confirmState.idleLabel}
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={busy || !canClearProductSelection(optionId)}
              onClick={() => void clearProductSelection(row)}
            >
              Clear
            </Button>
          </div>
        </div>
      )
    },
    [
      clearProductSelection,
      confirmProductLink,
      getCatalogOptionsForRow,
      getOptionIdForRow,
      handleProductOptionChange,
      isVendorItemBusy,
    ],
  )

  const closeRowModal = useCallback(() => {
    setPackEditorTarget(null)
    setModalRow(null)
  }, [])

  const keyboardActions = useMemo<PurchasingReviewKeyboardActions>(
    () => ({
      setShowFilters,
      setShowSort,
      setShowColumns,
      setShowBulkEdit,
      setShowShortcutsModal: setShowShortcuts,
      setGroupBy,
      clearSelection: () => setSelectedRowKeys(new Set()),
      focusSearch: () => {
        const el = document.getElementById(searchInputId)?.querySelector('input')
        if (el instanceof HTMLInputElement) el.focus()
      },
      closeRowModal,
      closeCreateProduct: () => setCreateProductRow(null),
      closePackEditor: () => setPackEditorTarget(null),
      moveCursor: (direction) => {
        const count = navItems.length
        setCursorIndex((prev) => nextCursorIndex(prev, count, direction))
      },
      toggleCursorSelection: () => {
        const index = cursorIndexRef.current
        const item = index != null ? navItems[index] : null
        if (!item) return
        toggleRowSelection(item.tableRowKey, !selectedRowKeys.has(item.tableRowKey), undefined, index ?? undefined)
      },
      openCursorRow: () => {
        const row = getCursorRow()
        if (!row || isAutoLinkedReviewRow(row)) return
        void openModalForRow(row)
      },
      approveCursorOrSelection: () => {
        const selectedRows = queueRows.filter((row) => selectedLineIds.has(row.id))
        const cursorRow = getCursorRow()
        const targets = selectedRows.length > 0 ? selectedRows : cursorRow ? [cursorRow] : []
        void handleApproveLines(targets.map((row) => row.id))
      },
      linkCursorOrSelection: () => {
        const selectedRows = queueRows.filter((row) => selectedLineIds.has(row.id))
        if (selectedRows.length > 0) {
          void handleBulkConfirmLink()
          return
        }
        const row = modalRow ?? getCursorRow()
        if (!row || !canConfirmProductLink(row, getOptionIdForRow(row))) return
        void confirmProductLink(row)
      },
      openProductPicker: () => {
        const row = getCursorRow()
        if (!row) return
        if (selectedRowKeys.size === 0) {
          const index = cursorIndexRef.current
          const item = index != null ? navItems[index] : null
          if (item) setSelectedRowKeys(new Set([item.tableRowKey]))
        }
        setBulkLinkRow(row)
      },
      openBulkEdit: () => {
        if (selectedLineIds.size === 0) return
        setBulkEditQty('')
        setBulkEditUnitPrice('')
        setBulkEditPack('')
        setShowBulkEdit(true)
      },
      modalPrevRow: () => {
        if (!modalRow) return
        const index = navItems.findIndex((item) => item.row.id === modalRow.id)
        const nextIndex = nextCursorIndex(index >= 0 ? index : null, navItems.length, 'up')
        if (nextIndex == null) return
        void openModalForRow(navItems[nextIndex]!.row)
      },
      modalNextRow: () => {
        if (!modalRow) return
        const index = navItems.findIndex((item) => item.row.id === modalRow.id)
        const nextIndex = nextCursorIndex(index >= 0 ? index : null, navItems.length, 'down')
        if (nextIndex == null) return
        void openModalForRow(navItems[nextIndex]!.row)
      },
    }),
    [
      closeRowModal,
      confirmProductLink,
      getCursorRow,
      getOptionIdForRow,
      handleApproveLines,
      handleBulkConfirmLink,
      modalRow,
      navItems,
      openModalForRow,
      queueRows,
      selectedLineIds,
      selectedRowKeys,
      setCursorIndex,
      toggleRowSelection,
    ],
  )

  const keyboardContext = useMemo<PurchasingReviewKeyboardContext>(
    () => ({
      showFilters,
      showSort,
      showColumns,
      showRowModal: modalRow != null,
      showCreateProduct: createProductRow != null,
      showPackEditor: packEditorTarget != null,
      showBulkEdit,
      showShortcutsModal: showShortcuts,
      groupBy,
      hasSelection: selectedRowKeys.size > 0,
      modalOpen: modalRow != null,
    }),
    [
      createProductRow,
      groupBy,
      modalRow,
      packEditorTarget,
      selectedRowKeys.size,
      showBulkEdit,
      showColumns,
      showFilters,
      showShortcuts,
      showSort,
    ],
  )

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      handlePurchasingReviewKeyDown(event, keyboardContext, keyboardActions)
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [keyboardActions, keyboardContext])

  const handleCreateProductSubmit = useCallback(
    async (payload: Parameters<typeof createPurchasingProduct>[0]) => {
      if (!createProductRow) return
      const row = createProductRow
      try {
        const created = await createPurchasingProduct(payload)
        const option: SelectOption = {
          id: `product:${created.id}`,
          label: created.name,
          group: 'Products',
        }
        setCreatedProductOptions((prev) => [option, ...prev])
        const matchResult = await matchPurchasingVendorItem(row.vendor_item_id, {
          product_id: created.id,
        })
        applyCreateAndMatchLocally(row, created, matchResult)
        setCreateProductRow(null)
      } catch (err) {
        const candidates = parseDuplicateCandidates(err)
        if (candidates) {
          setDuplicateConflict({ kind: 'create', row, payload, candidates })
          return
        }
        toast.error(formatApiError(err, 'Failed to create product'))
        throw err
      }
    },
    [applyCreateAndMatchLocally, createProductRow],
  )

  const exportReviewCsv = useCallback(() => {
    const selected = sortedQueueRows.filter((row) => selectedLineIds.has(row.id))
    const rows = selected.length > 0 ? selected : sortedQueueRows
    const { headers, rows: matrix } = buildPurchasingReviewExportMatrix(rows)
    exportPurchasingTableCsv('purchasing-review-queue.csv', headers, matrix)
  }, [selectedLineIds, sortedQueueRows])

  async function exportReviewToGoogleSheet() {
    if (sortedQueueRows.length === 0 || isExporting) return
    setIsExporting(true)
    try {
      const { headers, rows } = buildPurchasingReviewExportMatrix(sortedQueueRows)
      const result = await exportPurchasingTableToGoogleSheet('Purchasing review queue', headers, rows)
      toast.success(`Created Google Sheet: ${result.name}`)
      window.open(result.url, '_blank', 'noopener,noreferrer')
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to export review queue to Google Sheet'))
    } finally {
      setIsExporting(false)
    }
  }

  const allVisibleSelected =
    visibleRowKeys.length > 0 && visibleRowKeys.every((key) => selectedRowKeys.has(key))
  const someVisibleSelected = visibleRowKeys.some((key) => selectedRowKeys.has(key))

  const selectedRows = useMemo(
    () => queueRows.filter((row) => selectedLineIds.has(row.id)),
    [queueRows, selectedLineIds],
  )

  const bulkActionsForSelection = useMemo(
    () =>
      reviewBulkActionsForAllRows(
        selectedRows,
        getSelectionForRow,
        segment,
        catalogProducts,
        catalogMeezIngredients,
        getOptionIdForRow,
      ),
    [
      catalogMeezIngredients,
      catalogProducts,
      getOptionIdForRow,
      getSelectionForRow,
      segment,
      selectedRows,
    ],
  )

  const checkboxHeaderStyle = useMemo((): CSSProperties => {
    return {
      position: 'sticky',
      left: 0,
      top: 0,
      zIndex: 21,
      width: REVIEW_CHECKBOX_COLUMN_WIDTH,
      minWidth: REVIEW_CHECKBOX_COLUMN_WIDTH,
      maxWidth: REVIEW_CHECKBOX_COLUMN_WIDTH,
      backgroundColor: 'var(--color-white, #fff)',
    }
  }, [])

  const checkboxCellStyle = useMemo((): CSSProperties => {
    return {
      position: 'sticky',
      left: 0,
      zIndex: 7,
      width: REVIEW_CHECKBOX_COLUMN_WIDTH,
      minWidth: REVIEW_CHECKBOX_COLUMN_WIDTH,
      maxWidth: REVIEW_CHECKBOX_COLUMN_WIDTH,
    }
  }, [])

  const groupHeaderCheckboxStyle = useMemo((): CSSProperties => {
    return {
      position: 'sticky',
      top: 'var(--review-queue-thead-height)',
      left: 0,
      zIndex: 18,
      width: REVIEW_CHECKBOX_COLUMN_WIDTH,
      minWidth: REVIEW_CHECKBOX_COLUMN_WIDTH,
      maxWidth: REVIEW_CHECKBOX_COLUMN_WIDTH,
      backgroundColor: 'rgb(249 250 251)',
    }
  }, [])

  const groupHeaderLabelStyle = useMemo((): CSSProperties => {
    return {
      position: 'sticky',
      top: 'var(--review-queue-thead-height)',
      left: REVIEW_CHECKBOX_COLUMN_WIDTH,
      zIndex: 17,
      overflow: 'visible',
      backgroundColor: 'rgb(249 250 251)',
    }
  }, [])

  const headerStyle = useCallback(
    (columnKey: ReviewQueueColumnKey): CSSProperties => {
      const isPinned = isReviewPinnedColumn(columnKey)
      const style: CSSProperties = {
        ...(allowColumnResize ? fixedColumnStyle(getWidth(columnKey)) : {}),
        position: 'sticky',
        top: 0,
        zIndex: isPinned ? 20 : 10,
        backgroundColor: 'var(--color-white, #fff)',
      }
      if (isPinned) {
        style.left = reviewPinnedStickyLeft(columnKey, visibleColumnKeys, getWidth)
      }
      return style
    },
    [allowColumnResize, getWidth, visibleColumnKeys],
  )

  const cellStyle = useCallback(
    (columnKey: ReviewQueueColumnKey): CSSProperties | undefined => {
      const baseStyle = allowColumnResize ? fixedColumnStyle(getWidth(columnKey)) : undefined
      if (!isReviewPinnedColumn(columnKey)) return baseStyle
      return {
        ...baseStyle,
        position: 'sticky',
        left: reviewPinnedStickyLeft(columnKey, visibleColumnKeys, getWidth),
        zIndex: 6,
      }
    },
    [allowColumnResize, getWidth, visibleColumnKeys],
  )

  const copyItemCode = useCallback((code: string) => {
    void navigator.clipboard.writeText(code).then(
      () => toast.success('Item code copied'),
      () => toast.error('Failed to copy item code'),
    )
  }, [])

  const tableStyle = useMemo((): CSSProperties | undefined => {
    if (!allowColumnResize) return undefined
    const width = visibleColumnKeys.reduce((sum, key) => sum + getWidth(key), 0) + 48
    return { width, minWidth: width, tableLayout: 'fixed' }
  }, [allowColumnResize, getWidth, visibleColumnKeys])

  const renderProductCell = (row: PurchasingReviewQueueRow, busy: boolean) => {
    return (
      <div onClick={(e) => e.stopPropagation()}>
        {renderProductPicker(row, 'review')}
        {!row.suggestion && !row.product_id ? (
          <Button
            variant="ghost"
            size="sm"
            disabled={busy}
            className="mt-1"
            onClick={() => void handleSuggest(row)}
          >
            Suggest
          </Button>
        ) : null}
      </div>
    )
  }

  async function handleSuggest(row: PurchasingReviewQueueRow) {
    setVendorItemBusy(row.vendor_item_id, true)
    try {
      const res = await suggestPurchasingVendorItem(row.vendor_item_id)
      setQueueRows((prev) =>
        prev.map((entry) =>
          entry.vendor_item_id === row.vendor_item_id
            ? {
                ...entry,
                suggestion: res.suggestion,
                match_error: res.match_error,
                match_status: res.match_status,
              }
            : entry,
        ),
      )
      if (res.match_error) {
        toast.error(res.match_error)
      }
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to load suggestion'))
    } finally {
      setVendorItemBusy(row.vendor_item_id, false)
    }
  }

  const handleUsePackUom = useCallback(
    async (row: PurchasingReviewQueueRow) => {
      if (row.product_id == null || row.atomic_uom == null) return
      try {
        await updatePurchasingProduct(row.product_id, { atomic_uom: row.atomic_uom })
        toast.success('Product UOM updated to match pack')
        void loadFirstPage()
      } catch (err) {
        toast.error(formatApiError(err, 'Failed to update product UOM'))
      }
    },
    [loadFirstPage],
  )

  const renderReasonActions = (row: PurchasingReviewQueueRow) => {
    const selection = getSelectionForRow(row)
    const reasons = queueRowReasons(row, selection, catalogProducts, catalogMeezIngredients)
    if (!reviewRowHasFlagActions(row, segment, selection, catalogProducts, catalogMeezIngredients)) {
      return null
    }
    const productUom = resolveRowProductAtomicUom(
      row,
      selection,
      catalogProducts,
      catalogMeezIngredients,
    )
    const showUomHelper =
      reasons.includes('uom_conflict') && row.atomic_uom != null && productUom != null
    const hasLinkedProduct =
      row.product_id != null || selection?.kind === 'product'
    return (
      <div className="flex flex-col gap-1" onClick={(e) => e.stopPropagation()}>
        {showUomHelper ? (
          <p className="text-xs text-gray-600">
            Pack UOM is {row.atomic_uom}; product UOM is {productUom}. For a weight pack, use the
            pack unit on the product. Only edit the pack if the printed pack is wrong.
          </p>
        ) : null}
        <div className="flex flex-wrap gap-1">
        {segment === 'done' ? (
          <Button size="sm" variant="primary" onClick={() => void handleUndoDone(row)}>
            Undo
          </Button>
        ) : null}
        {reasons.includes('missing_atomic_cost') ? (
          <Button size="sm" variant="primary" onClick={() => openPackEditor(row)}>
            Define pack
          </Button>
        ) : null}
        {reasons.includes('qty_assumed') ? (
          <Button size="sm" variant="primary" onClick={() => void handleApproveLines([row.id])}>
            Approve
          </Button>
        ) : null}
        {reasons.includes('cost_outlier') ? (
          <Button size="sm" variant="primary" onClick={() => void handleApproveLines([row.id])}>
            Approve
          </Button>
        ) : null}
        {reasons.includes('needs_product') ? (
          <Button
            size="sm"
            variant="primary"
            disabled={!canConfirmProductLink(row, getOptionIdForRow(row))}
            onClick={() => void confirmProductLink(row)}
          >
            {getOptionIdForRow(row)?.startsWith('new:') ? 'Confirm create' : 'Confirm link'}
          </Button>
        ) : null}
        {reasons.includes('uom_conflict') ? (
          hasLinkedProduct ? (
            <>
              <Button size="sm" variant="primary" onClick={() => openPackEditor(row)}>
                Edit pack
              </Button>
              {row.product_id != null && row.atomic_uom != null ? (
                <Button
                  size="sm"
                  variant="primary"
                  onClick={() => void handleUsePackUom(row)}
                >
                  Use pack UOM
                </Button>
              ) : null}
            </>
          ) : (
            <Button size="sm" variant="primary" onClick={() => setBulkLinkRow(row)}>
              Link product
            </Button>
          )
        ) : null}
        </div>
      </div>
    )
  }

  const renderColumnCell = (
    columnKey: ReviewQueueColumnKey,
    row: PurchasingReviewQueueRow,
    busy: boolean,
  ) => {
    const columnStyle = cellStyle(columnKey)
    const pinnedClass = isReviewPinnedColumn(columnKey)
      ? reviewPinnedCellClassName(columnKey, visibleColumnKeys)
      : ''
    const scrollableClass =
      !isReviewPinnedColumn(columnKey) && columnKey !== 'product' ? 'review-scrollable-cell' : ''
    const cellClassName = [pinnedClass, scrollableClass].filter(Boolean).join(' ')
    const suggestion = row.suggestion
    switch (columnKey) {
      case 'vendor':
        return (
          <td key={columnKey} className={cellClassName} style={columnStyle}>
            <div className="flex flex-wrap items-center gap-1">
              <span>{formatQboVendorLabel(row.qbo_vendor_id, vendorNameMap)}</span>
              {isAutoLinkedReviewRow(row) ? (
                <span className={renderPurchasingStatusBadge(purchasingMatchStatusBadge('auto'))}>
                  {purchasingMatchStatusBadge('auto').label}
                </span>
              ) : null}
            </div>
          </td>
        )
      case 'vendor_item_code':
        return (
          <td key={columnKey} className={cellClassName} style={columnStyle}>
            {row.vendor_item_code ? (
              <button
                type="button"
                className="text-left text-sm text-brand-700 hover:text-brand-800 hover:underline"
                onClick={(e) => {
                  e.stopPropagation()
                  copyItemCode(row.vendor_item_code!)
                }}
              >
                {row.vendor_item_code}
              </button>
            ) : (
              '—'
            )}
          </td>
        )
      case 'description':
        return (
          <td key={columnKey} className={cellClassName} style={columnStyle}>
            <span className="block truncate text-sm">
              {row.description ?? '—'}
            </span>
          </td>
        )
      case 'pack':
        return <td key={columnKey} className={cellClassName} style={columnStyle}>{row.pack_text ?? '—'}</td>
      case 'product':
        return <td key={columnKey} style={columnStyle}>{renderProductCell(row, busy)}</td>
      case 'confidence':
        return (
          <td key={columnKey} className={cellClassName} style={columnStyle}>
            {suggestion?.confidence ? (
              <HoverHelp label={suggestion.confidence} aria-label="Match reasoning">
                {suggestion.reasoning ?? 'No reasoning provided'}
              </HoverHelp>
            ) : (
              '—'
            )}
          </td>
        )
      case 'invoice':
        return (
          <td key={columnKey} className={cellClassName} style={columnStyle}>
            <div className="text-sm">{row.document_reference_number ?? '—'}</div>
            <div className="text-xs text-gray-500">{formatDateYmd(row.invoice_date)}</div>
          </td>
        )
      case 'quantity':
        return <td key={columnKey} className={cellClassName} style={columnStyle}>{row.quantity ?? '—'}</td>
      case 'unit_price':
        return <td key={columnKey} className={cellClassName} style={columnStyle}>{formatMoney(row.unit_price)}</td>
      case 'amount':
        return <td key={columnKey} className={cellClassName} style={columnStyle}>{formatMoney(row.extended_price)}</td>
      case 'cost_per_atomic':
        return (
          <td key={columnKey} className={cellClassName} style={columnStyle}>
            <div className="tabular-nums">{formatCostPerAtomic(row.cost_per_atomic_unit)}</div>
            {row.median_cost_per_atomic ? (
              <div className="text-xs text-gray-500 tabular-nums">
                med {formatCostPerAtomic(row.median_cost_per_atomic)}
              </div>
            ) : null}
          </td>
        )
      case 'reasons':
        return (
          <td key={columnKey} className={cellClassName} style={columnStyle}>
            <div className="flex flex-wrap gap-1">
              {queueRowReasonBadges(
                row,
                getSelectionForRow(row),
                getOptionIdForRow(row),
                getCatalogOptionsForRow(row),
                catalogProducts,
                catalogMeezIngredients,
                catalogMeezIngredients,
              ).map((reason) => (
                <span key={reason} className={flagBadgeClass(reason)}>
                  {reasonLabel(reason)}
                </span>
              ))}
            </div>
          </td>
        )
      case 'approved':
        return (
          <td key={columnKey} className={cellClassName} style={columnStyle}>
            {formatChicagoDateTime(row.decided_at ?? row.updated_at ?? null)}
          </td>
        )
      case 'updated':
        return (
          <td key={columnKey} className={cellClassName} style={columnStyle}>
            {formatDateYmd(row.updated_at ?? row.invoice_date)}
          </td>
        )
      default:
        return null
    }
  }

  const virtualItems = rowVirtualizer.getVirtualItems()
  const paddingTop = virtualItems[0]?.start ?? 0
  const paddingBottom =
    rowVirtualizer.getTotalSize() - (virtualItems[virtualItems.length - 1]?.end ?? 0)
  const firstVisibleVirtualIndex = virtualItems[0]?.index ?? -1
  const stickyGroupHeader =
    groupBy !== 'none' ? stickyGroupHeaderAt(tableRows, firstVisibleVirtualIndex) : null

  const renderGroupHeaderRow = (
    entry: Extract<ReviewTableRow, { kind: 'group' }>,
    rowKey: string,
    options?: { stickyClone?: boolean; virtualIndex?: number },
  ) => {
    return (
      <tr
        key={rowKey}
        ref={options?.virtualIndex != null ? rowVirtualizer.measureElement : undefined}
        data-index={options?.virtualIndex}
        className={`group-header review-group-header-row${options?.stickyClone ? ' review-group-header-sticky-clone' : ''}`}
        aria-hidden={options?.stickyClone ? true : undefined}
      >
        <td
          className="table-sticky-pinned table-sticky-cell overflow-hidden px-2"
          style={groupHeaderCheckboxStyle}
        />
        <td
          colSpan={visibleColumnKeys.length}
          className="review-group-header-label table-sticky-pinned table-sticky-cell px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-600"
          style={groupHeaderLabelStyle}
        >
          <span className="whitespace-nowrap">
            {entry.label}
            <span className="ml-2 font-normal normal-case text-gray-400">({entry.count})</span>
          </span>
        </td>
      </tr>
    )
  }

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden">
      <div className="shrink-0 space-y-2 pt-3">
        <div className="border-b border-gray-200">
          <div className="flex flex-col justify-between gap-2 sm:flex-row sm:items-center">
            <UnderlineTabNav
              tabs={[
                { id: 'needs_review', label: 'Needs review' },
                { id: 'done', label: 'Approved' },
              ]}
              activeId={segment}
              onChange={(id) => setSegment(id as ReviewSegment)}
              ariaLabel="Review queue"
              className="min-w-0 flex-1 border-b-0"
            />
            {hasHealthMetrics ? (
              <p className="pb-2 text-xs text-gray-600 sm:pb-0">
                Vendor health:{' '}
                <span className="tabular-nums">{healthFirstSeen} first seen</span>
                {' · '}
                <span className={healthRepeat > 0 ? 'font-semibold tabular-nums text-amber-800' : 'tabular-nums'}>
                  {healthRepeat} repeat
                </span>
              </p>
            ) : showHealthNoData ? (
              <p className="pb-2 text-xs text-gray-500 sm:pb-0">Vendor health: no data yet</p>
            ) : null}
          </div>
        </div>
      </div>

      <TablePageShell
        className="min-h-0 flex-1"
        rowCount={
          <p className="mb-2 pt-4 text-xs text-gray-500 sm:text-sm">
            <span className="font-medium text-gray-700">{headerCountLabel}</span>
            {backgroundLoading ? ' · loading more…' : null}
            {textSearch.trim() || activeFilterCount > 0
              ? ` shown (of ${queueRows.length.toLocaleString('en-US')} loaded)`
              : null}
          </p>
        }
        toolbarLeft={
          <div id={searchInputId} className="max-w-md flex-1">
            <SearchInput
              value={textSearch}
              onChange={setTextSearch}
              placeholder="Search lines…"
              className="w-full"
            />
          </div>
        }
        toolbarRight={
          <TableToolbar
            groupBySlot={
              <GroupBySelect
                value={groupBy}
                onChange={(value) => {
                  setGroupBy(value as ReviewGroupBy)
                  if (document.activeElement instanceof HTMLElement) {
                    document.activeElement.blur()
                  }
                }}
                options={[...REVIEW_GROUP_BY_OPTIONS]}
                aria-label="Group review queue"
              />
            }
            filterCount={activeFilterCount}
            sortCount={sortSpecs.length}
            showFilters={showFilters}
            showSort={showSort}
            showColumns={showColumns}
            onToggleFilters={() => setShowFilters((open) => !open)}
            onToggleSort={() => setShowSort((open) => !open)}
            onToggleColumns={() => setShowColumns((open) => !open)}
            exportOptions={[
              {
                id: 'review-sheets',
                label: 'Google Sheet',
                onClick: () => void exportReviewToGoogleSheet(),
                disabled: sortedQueueRows.length === 0,
              },
              {
                id: 'review-csv',
                label: 'CSV',
                onClick: exportReviewCsv,
                disabled: sortedQueueRows.length === 0,
              },
            ]}
            defaultExportId="review-sheets"
            isExporting={isExporting}
            exportDisabled={sortedQueueRows.length === 0}
          />
        }
        filterPills={
          <ActiveFilterPillBar
            pills={filterPills}
            onRemovePill={(id) => setFilterTree((prev) => removeFilterPill(prev, id))}
            onClearAll={() => setFilterTree(null)}
          />
        }
      >
        <div ref={scrollRef} className="table-scroll-mobile h-full min-h-0 overflow-auto overscroll-x-contain">
          {loadingQueue && queueRows.length === 0 ? (
            <p className="py-12 text-center text-sm text-gray-500">Loading review queue…</p>
          ) : uniqueSortedQueueRows.length === 0 ? (
            <p className="py-12 text-center text-sm text-gray-500">
              {segment === 'done' ? 'No settled lines yet.' : 'Nothing needs review right now.'}
            </p>
          ) : (
            <table
              ref={tableRef}
              className={`table-forecast purchasing-review-queue-table border-separate border-spacing-0${allowColumnResize ? '' : ' min-w-full'}`}
              style={tableStyle}
            >
              <thead>
                <tr>
                  <th className="relative" style={checkboxHeaderStyle}>
                    <input
                      type="checkbox"
                      aria-label="Select all visible lines"
                      checked={allVisibleSelected}
                      ref={(el) => {
                        if (el) el.indeterminate = someVisibleSelected && !allVisibleSelected
                      }}
                      onChange={(e) => {
                        if (e.target.checked) setSelectedRowKeys(new Set(visibleRowKeys))
                        else setSelectedRowKeys(new Set())
                      }}
                    />
                  </th>
                  {visibleColumnKeys.map((columnKey) => (
                    <th
                      key={columnKey}
                      className={`relative${isReviewPinnedColumn(columnKey) ? ` ${reviewPinnedCellClassName(columnKey, visibleColumnKeys)}` : ''}`}
                      style={headerStyle(columnKey)}
                    >
                      <span className="inline-flex items-center pr-2">
                        {columnKey === 'product' ? (
                          <HoverHelp
                            label={REVIEW_QUEUE_COLUMN_LABELS.product}
                            aria-label="Product column help"
                          >
                            Ingredient items link to Meez; resale items (crackers, wine, tinned goods)
                            become products of their own.
                          </HoverHelp>
                        ) : (
                          REVIEW_QUEUE_COLUMN_LABELS[columnKey]
                        )}
                      </span>
                      {allowColumnResize ? (
                        <ColumnResizeHandle columnKey={columnKey} onStartResize={startResize} />
                      ) : null}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paddingTop > 0 ? (
                  <tr>
                    <td colSpan={visibleColumnKeys.length + 1} style={{ height: paddingTop, padding: 0 }} />
                  </tr>
                ) : null}
                {stickyGroupHeader
                  ? renderGroupHeaderRow(stickyGroupHeader, `sticky-group-${stickyGroupHeader.label}`, {
                      stickyClone: true,
                    })
                  : null}
                {virtualItems.map((virtualRow) => {
                  const entry = tableRows[virtualRow.index] as ReviewTableRow | undefined
                  if (!entry) return null
                  if (entry.kind === 'group') {
                    return renderGroupHeaderRow(entry, `group-${entry.label}-${virtualRow.index}`, {
                      virtualIndex: virtualRow.index,
                    })
                  }

                  const row = entry.row
                  const rowKey = tableRowKey(entry.groupLabel, row.id)
                  const itemIndex = itemIndexByTableRowKey.get(rowKey) ?? -1
                  const busy = isVendorItemBusy(row.vendor_item_id)
                  const selected = selectedRowKeys.has(rowKey)

                  return (
                    <tr
                      key={`${entry.groupLabel}-${row.id}-${virtualRow.index}`}
                      ref={rowVirtualizer.measureElement}
                      data-index={virtualRow.index}
                      data-row-index={itemIndex}
                      className={`cursor-pointer ${selected ? 'review-row-selected' : ''} ${itemIndex === cursorIndex ? 'row-cursor' : ''}`}
                      onClick={(e) => {
                        if ((e.target as HTMLElement).closest('button,input,select,[role="combobox"]')) return
                        if (itemIndex !== -1) setCursorIndex(itemIndex)
                        void openModalForRow(row)
                      }}
                    >
                      <td
                        className="table-sticky-pinned table-sticky-cell overflow-hidden px-2"
                        style={checkboxCellStyle}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          type="checkbox"
                          checked={selected}
                          aria-label={`Select line ${row.id}`}
                          onClick={(e) =>
                            toggleRowSelection(rowKey, !selected, e, itemIndex)
                          }
                          onChange={() => undefined}
                        />
                      </td>
                      {visibleColumnKeys.map((columnKey) =>
                        renderColumnCell(columnKey, row, busy),
                      )}
                    </tr>
                  )
                })}
                {paddingBottom > 0 ? (
                  <tr>
                    <td colSpan={visibleColumnKeys.length + 1} style={{ height: paddingBottom, padding: 0 }} />
                  </tr>
                ) : null}
              </tbody>
            </table>
          )}
        </div>
      </TablePageShell>

      <FilterBuilderDrawer
        open={showFilters}
        onClose={() => setShowFilters(false)}
        fields={filterFields}
        root={filterTree}
        onChange={setFilterTree}
        onClearAll={() => setFilterTree(null)}
      />
      <SortBuilder
        open={showSort}
        fields={reviewSortFields}
        sorts={sortSpecs}
        onChange={setSortSpecs}
        onClose={() => setShowSort(false)}
        onClearAll={() => setSortSpecs(REVIEW_QUEUE_DEFAULT_SORT)}
      />
      <ColumnPicker
        open={showColumns}
        fields={REVIEW_QUEUE_FIELD_DEFINITIONS}
        visibility={columnVisibility}
        onChange={(visibility) => {
          setColumnVisibility(visibility)
          saveColumnVisibility(PURCHASING_REVIEW_PAGE_KEY, visibility)
        }}
        onClose={() => setShowColumns(false)}
        onReset={() =>
          setColumnVisibility(getDefaultColumnVisibility(REVIEW_QUEUE_FIELD_DEFINITIONS))
        }
      />

      <PurchasingReviewBulkActionBar
        selectedCount={selectedRowKeys.size}
        bulkActions={bulkActionsForSelection}
        onClearSelection={() => setSelectedRowKeys(new Set())}
        onApprove={() => void handleApproveLines([...selectedLineIds])}
        onLink={() => void handleBulkConfirmLink()}
        onLinkProduct={() => {
          const first = queueRows.find((row) => selectedLineIds.has(row.id))
          if (first) setBulkLinkRow(first)
        }}
        onDefinePack={openPackEditorForSelection}
        onEdit={() => {
          setBulkEditQty('')
          setBulkEditUnitPrice('')
          setBulkEditPack('')
          setShowBulkEdit(true)
        }}
        onExport={exportReviewCsv}
      />

      {modalRow ? (
        <PurchasingSourceInvoiceModal
          open
          row={modalRow}
          onClose={closeRowModal}
          decidedBy={modalDecidedBy}
          decidedAt={modalDecidedAt}
          onSaved={(updated) => {
            if (segment === 'needs_review' && !rowBelongsOnNeedsReview(updated)) {
              setQueueRows((prev) => removeLineFromQueue(prev, updated.id))
              adjustPurchasingNavCount(-1)
              setModalRow((prev) => (prev?.id === updated.id ? null : prev))
              return
            }
            setQueueRows((prev) => applyLinePatchLocally(prev, updated.id, updated))
            setModalRow((prev) => (prev?.id === updated.id ? updated : prev))
          }}
          onPackRulingApplied={() => void loadFirstPage()}
          renderReasonActions={renderReasonActions}
          productPicker={renderProductPicker(modalRow, 'review-modal', {
            useViewportForPlacement: true,
          })}
          showUndo={segment === 'done'}
          onUndo={modalRow ? () => void handleUndoDone(modalRow) : undefined}
        />
      ) : null}

      {createProductRow ? (
        <PurchasingCreateProductDialog
          open
          initialName={createProductName}
          defaultAtomicUom={createProductRow.atomic_uom ?? 'ea'}
          products={catalogProducts}
          template={createProductRow.suggestion?.new_product}
          onClose={() => setCreateProductRow(null)}
          onSubmit={handleCreateProductSubmit}
        />
      ) : null}

      {duplicateConflict ? (
        <PurchasingDuplicateProductDialog
          open
          proposedName={
            duplicateConflict.kind === 'match'
              ? duplicateConflict.payload.new_product?.name
              : duplicateConflict.payload.name
          }
          candidates={duplicateConflict.candidates}
          busy={duplicateConflictBusy}
          onClose={() => {
            if (!duplicateConflictBusy) setDuplicateConflict(null)
          }}
          onUseExisting={(productId) => void handleDuplicateUseExisting(productId)}
          onCreateAnyway={() => void handleDuplicateCreateAnyway()}
        />
      ) : null}

      {bulkLinkRow ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setBulkLinkRow(null)} />
          <div className="relative w-full max-w-md rounded-lg bg-white p-4 shadow-xl">
            <h3 className="text-base font-semibold text-gray-900">Link product</h3>
            <div className="mt-3">
              <SearchableSelect
                id="bulk-link-product"
                options={getCatalogOptionsForRow(bulkLinkRow)}
                value={getOptionIdForRow(bulkLinkRow)}
                onChange={(id) => void handleBulkLink(bulkLinkRow, id)}
                placeholder="Choose product or Meez ingredient"
                allowCreate
                onCreateNew={(name) => {
                  setCreateProductRow(bulkLinkRow)
                  setCreateProductName(name)
                  setBulkLinkRow(null)
                  return Promise.resolve(null)
                }}
                onSearchChange={setCatalogSearch}
              />
            </div>
            <div className="mt-4 flex justify-end">
              <Button variant="ghost" onClick={() => setBulkLinkRow(null)}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      {showBulkEdit ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowBulkEdit(false)} />
          <div className="relative w-full max-w-md rounded-lg bg-white p-4 shadow-xl">
            <h3 className="text-base font-semibold text-gray-900">Bulk edit {selectedRowKeys.size} lines</h3>
            <div className="mt-3 space-y-2">
              <input className="input w-full" placeholder="Qty" value={bulkEditQty} onChange={(e) => setBulkEditQty(e.target.value)} />
              <input className="input w-full" placeholder="Unit price" value={bulkEditUnitPrice} onChange={(e) => setBulkEditUnitPrice(e.target.value)} />
              <input className="input w-full" placeholder="Pack text" value={bulkEditPack} onChange={(e) => setBulkEditPack(e.target.value)} />
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setShowBulkEdit(false)}>
                Cancel
              </Button>
              <Button onClick={() => void handleBulkEditSubmit()}>Apply</Button>
            </div>
          </div>
        </div>
      ) : null}

      <PurchasingReviewKeyboardShortcutsModal open={showShortcuts} onClose={() => setShowShortcuts(false)} />

      {packEditorTarget ? (
        <PurchasingVendorItemPackDialog
          open
          vendorItemIds={packEditorTarget.vendorItemIds}
          initialPackText={packEditorTarget.initialPackText}
          title={
            packEditorTarget.vendorItemIds.length > 1
              ? `Define pack for ${packEditorTarget.vendorItemIds.length} items`
              : 'Define pack'
          }
          onClose={() => setPackEditorTarget(null)}
          onSaved={handlePackSaved}
        />
      ) : null}
    </div>
  )
}
